import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
  Tooltip,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";

import { setToastMessage } from "../../store/slices/app/appSlice";
import { components } from "../../types/api";
import {
  useCreateAssociatedCampaignGoalMutation,
  useUpdateAssociatedCampaignGoalMutation,
  useCalculateGoalFinancialsMutation,
} from "../../store/apis/briefingApi";
import { useGetCurrenciesQuery } from "../../store/apis/currencyApi";
import { CountrySelect } from "../common/CountrySelect";
import { useUserRole } from "../../hooks/useUserRole";
import {
  canEditGoalField,
  canViewGoalField,
} from "../../utils/briefingPermissions";

// type BriefingObjective = components["schemas"]["BriefingObjective"];
type BriefingObjective = "CPC" | "CPM" | "CPMV" | "CPV" | "CPE" | "CPA" | "CPL";
type BriefingFunnel = components["schemas"]["_36_Enums.BriefingFunnel"];
type BriefingPlatform = components["schemas"]["BriefingPlatform"];

type AdserverFormat = "VIDEO" | "RICH_MEDIA_NO_VIDEO" | "BANNER";

const adserverCostByFormat: Record<AdserverFormat, number> = {
  VIDEO: 0.25,
  RICH_MEDIA_NO_VIDEO: 0.15,
  BANNER: 0.04,
};

const deriveAdserverFormat = (cost: number | null): AdserverFormat => {
  if (!cost && cost !== 0) {
    return "RICH_MEDIA_NO_VIDEO";
  }
  if (Math.abs(cost - adserverCostByFormat.BANNER) < 0.01) {
    return "BANNER";
  }
  if (Math.abs(cost - adserverCostByFormat.VIDEO) < 0.01) {
    return "VIDEO";
  }
  return "RICH_MEDIA_NO_VIDEO";
};

const DV360_AUTOTECH_PLATFORMS: BriefingPlatform[] = [
  "BEAPOLO_NETFLIX",
  "BEAPOLO_SAMSUNG_TV",
  "BEAPOLO_DISNEY_PLUS",
  "BEAPOLO_MOVISTAR",
];

type ApiErrorWithMessage = { data?: { message?: string } };

const extractApiErrorMessage = (error: unknown): string | undefined => {
  if (
    typeof error === "object" &&
    error !== null &&
    "data" in error &&
    typeof (error as ApiErrorWithMessage).data?.message === "string"
  ) {
    return (error as ApiErrorWithMessage).data?.message;
  }
  return undefined;
};

export type GoalFormState = {
  startDate: string;
  endDate: string;
  currency: string;
  margin: string;
  campaign: string;
  country: string;
  objective: BriefingObjective;
  funnel: BriefingFunnel;
  campaignConcept: string;
  orderName: string;
  lineName: string;
  expectedVolume: string;
  visibility: string;
  ctr: string;
  netUnitCost: string;
  zeotapDataCost: string;
  adserverCost: string;
  impressionTrackerCost: string;
  clickTrackerCost: string;
  // intermediaryFee: string;
  adtechCost: string;
  grossUnitCost: string;
  platformInversion: string;
  totalInversion: string;
  status: string;
  notes: string;
};

type GoalFormErrors = Partial<Record<keyof GoalFormState, string>>;

type GoalFieldConfig = {
  field: keyof GoalFormState;
  labelKey: string;
  fallback: string;
  type?: "text" | "number" | "textarea";
  required?: boolean;
  disabled?: boolean;
  tooltip?: string;
};

const defaultFormState: GoalFormState = {
  startDate: "",
  endDate: "",
  currency: "",
  margin: "",
  campaign: "",
  country: "",
  objective: "CPC",
  funnel: "AWARENESS",
  campaignConcept: "",
  orderName: "",
  lineName: "",
  expectedVolume: "",
  visibility: "",
  ctr: "",
  netUnitCost: "",
  zeotapDataCost: "",
  adserverCost: "",
  impressionTrackerCost: "",
  clickTrackerCost: "",
  // intermediaryFee: "",
  adtechCost: "",
  grossUnitCost: "",
  platformInversion: "",
  totalInversion: "",
  status: "",
  notes: "",
};

const numericFields: Array<keyof GoalFormState> = [
  "margin",
  "expectedVolume",
  "visibility",
  "ctr",
  "netUnitCost",
  "zeotapDataCost",
  "adserverCost",
  "impressionTrackerCost",
  "clickTrackerCost",
  // "intermediaryFee",
  "adtechCost",
  "grossUnitCost",
  "platformInversion",
  "totalInversion",
];

const requiredTextFields: Array<keyof GoalFormState> = [
  "campaign",
  "country",
  "campaignConcept",
  "orderName",
  "lineName",
];

interface GoalDialogProps {
  open: boolean;
  briefingId: string;
  associatedId: string;
  initialValues?: Partial<GoalFormState> | null;
  mode: "create" | "edit";
  onClose: () => void;
  onSuccess?: () => void;
  companyName: string;
  campaignName: string;
  subcampaignName?: string | null;
  brandOrProduct: string;
  isBeApoloCompany: boolean;
  platformType?: BriefingPlatform;
  /** Briefing total budget — used to warn when totalInversion exceeds it */
  briefingTotalBudget?: number;
  campaignType?: string | null;
  briefingStatus?: components["schemas"]["BriefingStatus"];
}

const GoalDialog: React.FC<GoalDialogProps> = ({
  open,
  onClose,
  briefingId,
  associatedId,
  initialValues,
  mode,
  onSuccess,
  companyName,
  campaignName,
  subcampaignName,
  brandOrProduct,
  isBeApoloCompany,
  platformType,
  briefingTotalBudget,
  campaignType,
  briefingStatus,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();

  const { role } = useUserRole();

  // Role-based field visibility and edit permissions using centralized permission logic
  const shouldShowField = (fieldKey: string): boolean => {
    if (!role) return false;
    return canViewGoalField(role, fieldKey);
  };

  const shouldDisableField = (fieldKey: string): boolean => {
    if (!role) return true;
    return !canEditGoalField(role, fieldKey, briefingStatus);
  };

  const [formState, setFormState] = useState<GoalFormState>(defaultFormState);
  const [errors, setErrors] = useState<GoalFormErrors>({});
  const [adserverFormat, setAdserverFormat] = useState<AdserverFormat | null>(
    null
  );
  const [adtechCostManuallyEdited, setAdtechCostManuallyEdited] =
    useState(false);
  const [adserverCostManuallyEdited, setAdserverCostManuallyEdited] =
    useState(false);

  const [createGoal, { isLoading: isCreating }] =
    useCreateAssociatedCampaignGoalMutation();
  const [updateGoal, { isLoading: isUpdating }] =
    useUpdateAssociatedCampaignGoalMutation();

  const [calculateFinancials] = useCalculateGoalFinancialsMutation();
  const [isCalculatingMath, setIsCalculatingMath] = useState(false);

  type GoalPayload = Parameters<typeof createGoal>[0]["body"];
  const [pendingPayload, setPendingPayload] = useState<GoalPayload | null>(
    null
  );

  const isLoading = isCreating || isUpdating;

  const {
    data: currenciesResponse,
    isError: currenciesError,
    isLoading: isCurrenciesLoading,
  } = useGetCurrenciesQuery();

  useEffect(() => {
    if (currenciesError) {
      dispatch(
        setToastMessage({
          message: t(
            "briefingsView.goalForm.currencyLoadError",
            "Unable to load currencies"
          ),
          severity: "error",
        })
      );
    }
  }, [currenciesError, dispatch, t]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (mode === "edit" && initialValues) {
      const normalizedState: GoalFormState = {
        ...defaultFormState,
        ...initialValues,
        startDate: initialValues.startDate
          ? initialValues.startDate.split("T")[0]
          : "",
        endDate: initialValues.endDate
          ? initialValues.endDate.split("T")[0]
          : "",
        margin:
          initialValues.margin != null
            ? Number(initialValues.margin).toFixed(2)
            : "",
        expectedVolume:
          initialValues.expectedVolume != null
            ? Number(initialValues.expectedVolume).toFixed(6)
            : "",
        visibility:
          initialValues.visibility != null
            ? Number(initialValues.visibility).toFixed(2)
            : "",
        ctr:
          initialValues.ctr != null ? Number(initialValues.ctr).toFixed(2) : "",
        netUnitCost:
          initialValues.netUnitCost != null
            ? Number(initialValues.netUnitCost).toFixed(4)
            : "",
        zeotapDataCost:
          initialValues.zeotapDataCost != null
            ? Number(initialValues.zeotapDataCost).toFixed(4)
            : "",
        adserverCost:
          initialValues.adserverCost != null
            ? Number(initialValues.adserverCost).toFixed(4)
            : "",
        impressionTrackerCost:
          initialValues.impressionTrackerCost != null
            ? Number(initialValues.impressionTrackerCost).toFixed(4)
            : "",
        clickTrackerCost:
          initialValues.clickTrackerCost != null
            ? Number(initialValues.clickTrackerCost).toFixed(4)
            : "",
        adtechCost:
          initialValues.adtechCost != null
            ? Number(initialValues.adtechCost).toFixed(4)
            : "",
        grossUnitCost:
          initialValues.grossUnitCost != null
            ? Number(initialValues.grossUnitCost).toFixed(4)
            : "",
        platformInversion:
          initialValues.platformInversion != null
            ? Number(initialValues.platformInversion).toFixed(2)
            : "",
        totalInversion:
          initialValues.totalInversion != null
            ? Number(initialValues.totalInversion).toFixed(2)
            : "",
      };
      setFormState(normalizedState);
      const currentCost = parseFloat(normalizedState.adserverCost);
      setAdserverFormat(
        isBeApoloCompany
          ? deriveAdserverFormat(
              Number.isFinite(currentCost) ? currentCost : null
            )
          : null
      );
    } else {
      const isDirectosProprios = platformType === "BEAPOLO";
      const baseState: GoalFormState = {
        ...defaultFormState,
        adserverCost: isBeApoloCompany
          ? adserverCostByFormat.RICH_MEDIA_NO_VIDEO.toFixed(2)
          : defaultFormState.adserverCost,
        ctr: isDirectosProprios ? "0.30" : defaultFormState.ctr,
        visibility: isDirectosProprios ? "80" : defaultFormState.visibility,
      };
      setFormState(baseState);
      setAdserverFormat(isBeApoloCompany ? "RICH_MEDIA_NO_VIDEO" : null);
    }

    setAdtechCostManuallyEdited(false);
    setAdserverCostManuallyEdited(false);
    setErrors({});
  }, [open, mode, initialValues, isBeApoloCompany, platformType]);

  // Amazon Prime campaigns carry PRIME in the order and line names
  const primeTag = platformType === "BEAPOLO_AMAZON_PRIME" ? "PRIME" : "";

  // Calculate Line Name
  useEffect(() => {
    const ctry = formState.country.trim();
    const objective = formState.objective;
    const funnel = formState.funnel;

    if (companyName && objective) {
      const parts = [
        companyName,
        campaignName?.trim(),
        brandOrProduct?.trim(),
        subcampaignName?.trim(),
        ctry,
        funnel,
        campaignType,
        primeTag,
        objective,
      ];
      const newLineName = parts
        .filter((p) => Boolean(p) && p !== "UNDEFINED")
        .join("_");
      setFormState((prev) => ({
        ...prev,
        lineName: newLineName,
      }));
    }
  }, [
    companyName,
    campaignName,
    brandOrProduct,
    subcampaignName,
    formState.country,
    formState.funnel,
    campaignType,
    primeTag,
    formState.objective,
  ]);

  // Calculate Order Name
  useEffect(() => {
    const ctry = formState.country.trim();
    const funnel = formState.funnel;

    if (companyName && funnel) {
      const parts = [
        companyName,
        campaignName?.trim(),
        brandOrProduct?.trim(),
        subcampaignName?.trim(),
        ctry,
        funnel,
        campaignType,
        primeTag,
      ];
      const newOrderName = parts
        .filter((p) => Boolean(p) && p !== "UNDEFINED")
        .join("_");
      setFormState((prev) => ({
        ...prev,
        orderName: newOrderName,
      }));
    }
  }, [
    companyName,
    campaignName,
    brandOrProduct,
    subcampaignName,
    formState.country,
    formState.funnel,
    campaignType,
    primeTag,
  ]);

  // Calculate Campaign Concept
  useEffect(() => {
    if (!open) return;
    // campaignName/brandOrProduct are passed from the briefing
    if (campaignName || brandOrProduct || subcampaignName) {
      const parts = [
        campaignName?.trim(),
        brandOrProduct?.trim(),
        subcampaignName?.trim(),
      ];
      const newConcept = parts.filter(Boolean).join("_");

      if (newConcept !== formState.campaignConcept) {
        setFormState((prev) => ({
          ...prev,
          campaignConcept: newConcept,
        }));
      }
    }
  }, [
    open,
    campaignName,
    brandOrProduct,
    subcampaignName,
    formState.campaignConcept,
  ]);

  // Calculate Goal Campaign Name
  useEffect(() => {
    const ctry = formState.country.trim();
    const funnel = formState.funnel;

    if (companyName && funnel) {
      const parts = [
        companyName,
        campaignName?.trim(),
        brandOrProduct?.trim(),
        subcampaignName?.trim(),
        ctry,
        funnel,
      ];
      const newCampaignName = parts
        .filter((p) => Boolean(p) && p !== "UNDEFINED")
        .join("_");
      setFormState((prev) => ({
        ...prev,
        campaign: newCampaignName,
      }));
    }
  }, [
    companyName,
    campaignName,
    brandOrProduct,
    subcampaignName,
    formState.country,
    formState.funnel,
  ]);

  const objectives = useMemo<BriefingObjective[]>(
    () => ["CPC", "CPM", "CPMV", "CPV", "CPE", "CPA", "CPL"],
    []
  );

  const funnels = useMemo<BriefingFunnel[]>(
    () => ["AWARENESS", "CONSIDERATION", "PERFORMANCE"],
    []
  );

  const adtechPercentage = useMemo(() => {
    if (!platformType) {
      return null;
    }
    if (
      platformType === "BEAPOLO_AMAZON_PRIME" ||
      platformType === "BEAPOLO_TWITCH"
    ) {
      return 0.05;
    }
    if (DV360_AUTOTECH_PLATFORMS.includes(platformType)) {
      return 0.15;
    }
    return null;
  }, [platformType]);

  useEffect(() => {
    if (!open) return;
    const canSeeMargin = role ? canViewGoalField(role, "margin") : false;

    const handler = setTimeout(async () => {
      setIsCalculatingMath(true);
      try {
        const res = await calculateFinancials({
          platform: platformType ?? "",
          campaignType: campaignType ?? "",
          objective: formState.objective,
          grossUnitCost: parseFloat(formState.grossUnitCost) || 0,
          margin: parseFloat(formState.margin) || 0,
          totalInversion: parseFloat(formState.totalInversion) || 0,
          visibility: parseFloat(formState.visibility) || 0,
          ctr: parseFloat(formState.ctr) || 0,
          netUnitCost: parseFloat(formState.netUnitCost) || 0,
          adserverCost: parseFloat(formState.adserverCost) || 0,
          adtechCost: parseFloat(formState.adtechCost) || 0,
          impressionTrackerCost:
            parseFloat(formState.impressionTrackerCost) || 0,
          clickTrackerCost: parseFloat(formState.clickTrackerCost) || 0,
          zeotapDataCost: parseFloat(formState.zeotapDataCost) || 0,
          autoAdserverCost:
            !adserverCostManuallyEdited &&
            (role === "ADMIN" || role === "PRICING"),
          autoAdtechCost: !adtechCostManuallyEdited,
        }).unwrap();

        setFormState((prev) => {
          let updated = { ...prev };
          let changed = false;

          const checkAndSet = (
            key: keyof GoalFormState,
            newVal: number | string,
            decimals: number
          ) => {
            const formatted =
              typeof newVal === "number" ? newVal.toFixed(decimals) : newVal;
            if (prev[key] !== formatted) {
              (updated as Record<keyof GoalFormState, string>)[key] = formatted;
              changed = true;
            }
          };

          checkAndSet("visibility", res.visibility, 2);
          checkAndSet("ctr", res.ctr, 2);
          if (!adserverCostManuallyEdited)
            checkAndSet("adserverCost", res.adserverCost, 4);
          if (!adtechCostManuallyEdited)
            checkAndSet("adtechCost", res.adtechCost, 4);
          checkAndSet("expectedVolume", res.expectedVolume, 6);
          checkAndSet("platformInversion", res.platformInversion, 2);
          if (canSeeMargin) checkAndSet("netUnitCost", res.netUnitCost, 4);

          return changed ? updated : prev;
        });
      } catch (err) {
        console.error("Failed to calculate financials", err);
      } finally {
        setIsCalculatingMath(false);
      }
    }, 400);

    return () => clearTimeout(handler);
  }, [
    open,
    formState.grossUnitCost,
    formState.margin,
    formState.totalInversion,
    formState.objective,
    formState.impressionTrackerCost,
    formState.clickTrackerCost,
    formState.zeotapDataCost,
    formState.ctr,
    formState.visibility,
    formState.adserverCost,
    formState.adtechCost,
    formState.netUnitCost,
    platformType,
    campaignType,
    adserverCostManuallyEdited,
    adtechCostManuallyEdited,
    calculateFinancials,
    role,
  ]);

  const handleClose = () => {
    if (isLoading) {
      return;
    }
    setErrors({});
    onClose();
  };

  const handleAdserverFormatChange = (
    _event: React.MouseEvent<HTMLElement>,
    next: AdserverFormat | null
  ) => {
    if (!next) {
      return;
    }
    setAdserverFormat(next);
    setFormState((prev) => ({
      ...prev,
      adserverCost: String(Number(adserverCostByFormat[next].toFixed(4))),
    }));
  };

  const handleReapplyAdtech = () => {
    setAdtechCostManuallyEdited(false);
    setAdserverCostManuallyEdited(false);
  };

  const handleFieldChange = (
    field: keyof GoalFormState,
    value: string
  ): void => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
    if (field === "adtechCost") {
      setAdtechCostManuallyEdited(true);
    }
    if (field === "adserverCost") {
      setAdserverCostManuallyEdited(true);
    }
  };

  const validateForm = (): boolean => {
    const nextErrors: GoalFormErrors = {};

    requiredTextFields.forEach((field) => {
      // Only validate if the user can actually see and edit the field
      if (!shouldShowField(field) || shouldDisableField(field)) {
        return;
      }
      if (!formState[field].trim()) {
        nextErrors[field] = t("validation.required", "This field is required");
      }
    });

    numericFields.forEach((field) => {
      // Only validate if the user can actually see and edit the field
      if (!shouldShowField(field) || shouldDisableField(field)) {
        return;
      }
      const val = formState[field];
      if (!val || !val.trim()) {
        nextErrors[field] = t("validation.required", "This field is required");
        return;
      }
      if (Number.isNaN(Number(val))) {
        nextErrors[field] = t(
          "validation.invalidNumber",
          "Enter a valid number"
        );
      }
    });

    const canEditStartDate =
      !role || canEditGoalField(role, "startDate", briefingStatus);
    const canEditEndDate =
      !role || canEditGoalField(role, "endDate", briefingStatus);

    if (canEditStartDate) {
      if (!formState.startDate) {
        nextErrors.startDate = t(
          "validation.required",
          "This field is required"
        );
      } else {
        const d = new Date(formState.startDate);
        if (Number.isNaN(d.getTime())) {
          nextErrors.startDate = t("validation.invalidDate", "Invalid date");
        }
      }
    }

    if (canEditEndDate) {
      if (!formState.endDate) {
        nextErrors.endDate = t("validation.required", "This field is required");
      } else {
        const d = new Date(formState.endDate);
        if (Number.isNaN(d.getTime())) {
          nextErrors.endDate = t("validation.invalidDate", "Invalid date");
        }
      }

      if (
        !nextErrors.startDate &&
        !nextErrors.endDate &&
        formState.startDate &&
        formState.endDate &&
        new Date(formState.endDate) < new Date(formState.startDate)
      ) {
        nextErrors.endDate = t(
          "validation.endBeforeStart",
          "End date cannot be before start date"
        );
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const doSubmit = async (
    payload: Parameters<typeof createGoal>[0]["body"]
  ): Promise<void> => {
    try {
      if (mode === "create") {
        const cleanBriefingId = briefingId?.replace(/['"]/g, "") || "";
        const cleanAssociatedId = associatedId?.replace(/['"]/g, "") || "";
        await createGoal({
          params: {
            briefingId: cleanBriefingId,
            associatedId: cleanAssociatedId,
          },
          body: payload,
        }).unwrap();
        dispatch(
          setToastMessage({
            message: t(
              "briefingsView.goalCreateSuccess",
              "Goal created successfully"
            ),
            severity: "success",
          })
        );
      } else {
        const cleanBriefingId = briefingId?.replace(/['"]/g, "") || "";
        const cleanAssociatedId = associatedId?.replace(/['"]/g, "") || "";
        await updateGoal({
          params: {
            briefingId: cleanBriefingId,
            associatedId: cleanAssociatedId,
          },
          body: payload,
        }).unwrap();
        dispatch(
          setToastMessage({
            message: t(
              "briefingsView.goalUpdateSuccess",
              "Goal updated successfully"
            ),
            severity: "success",
          })
        );
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      const apiMessage = extractApiErrorMessage(error);
      const defaultError =
        mode === "create"
          ? t("briefingsView.goalCreateError", "Failed to create goal")
          : t("briefingsView.goalUpdateError", "Failed to update goal");
      dispatch(
        setToastMessage({
          message: apiMessage ?? defaultError,
          severity: "error",
        })
      );
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (!validateForm()) {
      return;
    }

    const rawPayload: Record<string, unknown> = {
      startDate: formState.startDate
        ? new Date(formState.startDate).toISOString()
        : undefined,
      endDate: formState.endDate
        ? new Date(formState.endDate).toISOString()
        : undefined,
      currency: formState.currency?.trim() || undefined,
      margin: Number(formState.margin) || 0,
      campaign: formState.campaign?.trim() || "",
      country: formState.country?.trim() || "",
      objective: formState.objective,
      funnel: formState.funnel,
      campaignConcept: formState.campaignConcept?.trim() || "",
      orderName: formState.orderName?.trim() || "",
      lineName: formState.lineName?.trim() || "",
      expectedVolume: Number(formState.expectedVolume) || 0,
      visibility: Number(formState.visibility) || 0,
      ctr: Number(formState.ctr) || 0,
      netUnitCost: Number(formState.netUnitCost) || 0,
      zeotapDataCost: Number(formState.zeotapDataCost) || 0,
      adserverCost: Number(formState.adserverCost) || 0,
      impressionTrackerCost: Number(formState.impressionTrackerCost) || 0,
      clickTrackerCost: Number(formState.clickTrackerCost) || 0,
      adtechCost: Number(formState.adtechCost) || 0,
      grossUnitCost: Number(formState.grossUnitCost) || 0,
      platformInversion: Number(formState.platformInversion) || 0,
      totalInversion: Number(formState.totalInversion) || 0,
      status: formState.status?.trim() || null,
      notes: formState.notes?.trim() || null,
    };

    if (role) {
      Object.keys(rawPayload).forEach((key) => {
        if (!canEditGoalField(role, key, briefingStatus)) {
          delete rawPayload[key];
        }
      });
    }

    const payload = rawPayload as Parameters<typeof createGoal>[0]["body"];

    // Budget check — show confirmation instead of blocking
    if (
      briefingTotalBudget !== undefined &&
      Number(formState.totalInversion) > briefingTotalBudget
    ) {
      setPendingPayload(payload);
      return;
    }

    await doSubmit(payload);
  };

  const baseInfoFields: GoalFieldConfig[] = [
    {
      field: "country",
      labelKey: "briefingsView.goalForm.country",
      fallback: "Country",
      required: true,
    },
    // Calculated fields at the end
    {
      field: "campaign",
      labelKey: "briefingsView.goalForm.campaign",
      fallback: "Campaign",
      required: true,
      disabled: true,
      tooltip: "Company_Concept_Country_Funnel",
    },
    {
      field: "campaignConcept",
      labelKey: "briefingsView.goalForm.campaignConcept",
      fallback: "Campaign concept",
      required: true,
      disabled: true,
      tooltip: "Campaign_Product_Audience_Month_Year",
    },
    {
      field: "orderName",
      labelKey: "briefingsView.goalForm.orderName",
      fallback: "Order name",
      required: true,
      disabled: true,
      tooltip: "Company_Concept_Country_Funnel",
    },
    {
      field: "lineName",
      labelKey: "briefingsView.goalForm.lineName",
      fallback: "Line name",
      required: false,
      disabled: true,
      tooltip: "Company_Concept_Country_Funnel_Objective",
    },
  ];

  const performanceFields: GoalFieldConfig[] = [
    {
      field: "margin",
      labelKey: "briefingsView.goalForm.margin",
      fallback: "Margin",
      type: "number",
      required: true,
    },
    {
      field: "visibility",
      labelKey: "briefingsView.goalForm.visibility",
      fallback: "Visibility",
      type: "number",
      required: true,
    },
    {
      field: "ctr",
      labelKey: "briefingsView.goalForm.ctr",
      fallback: "CTR",
      type: "number",
      required: true,
    },
    // Calculated fields at the end
    {
      field: "expectedVolume",
      labelKey: "briefingsView.goalForm.expectedVolume",
      fallback: "Expected volume",
      type: "number",
      required: true,
      disabled: true,
      tooltip: "TotalInversion / GrossUnitCost (if CPM objective, * 1000)",
    },
    {
      field: "netUnitCost",
      labelKey: "briefingsView.goalForm.netUnitCost",
      fallback: "Net unit cost",
      type: "number",
      required: true,
      disabled: true,
      tooltip:
        "(GrossUnitCost) * (1 - Margin/100) - ClickTracker - ImprTracker - AdServer - Zeotap - AdtechCost",
    },
  ];

  const costFields: GoalFieldConfig[] = [
    {
      field: "zeotapDataCost",
      labelKey: "briefingsView.goalForm.zeotapDataCost",
      fallback: "Zeotap data cost",
      type: "number",
      required: true,
    },
    {
      field: "adserverCost",
      labelKey: "briefingsView.goalForm.adserverCost",
      fallback: "Adserver cost",
      type: "number",
      required: true,
    },
    {
      field: "impressionTrackerCost",
      labelKey: "briefingsView.goalForm.impressionTrackerCost",
      fallback: "Impression tracker cost",
      type: "number",
      required: true,
    },
    {
      field: "clickTrackerCost",
      labelKey: "briefingsView.goalForm.clickTrackerCost",
      fallback: "Click tracker cost",
      type: "number",
      required: true,
    },
    // {
    //   field: "intermediaryFee",
    //   labelKey: "briefingsView.goalForm.intermediaryFee",
    //   fallback: "Intermediary fee",
    //   type: "number",
    //   required: true,
    // },
    {
      field: "adtechCost",
      labelKey: "briefingsView.goalForm.adtechCost",
      fallback: "Adtech cost",
      type: "number",
      required: true,
    },
    {
      field: "grossUnitCost",
      labelKey: "briefingsView.goalForm.grossUnitCost",
      fallback: "Gross unit cost",
      type: "number",
      required: true,
    },
    {
      field: "totalInversion",
      labelKey: "briefingsView.goalForm.totalInversion",
      fallback: "Total inversion",
      type: "number",
      required: true,
    },
    // Calculated field at the end
    {
      field: "platformInversion",
      labelKey: "briefingsView.goalForm.platformInversion",
      fallback: "Platform inversion",
      type: "number",
      required: true,
      disabled: true,
      tooltip:
        "ExpectedVolume * NetUnitCost (if CPM objective, divided by 1000)",
    },
  ];

  const currencyOptions = useMemo(() => {
    if (!currenciesResponse || currenciesResponse.length === 0) {
      return [];
    }
    return currenciesResponse.map((currency) => ({
      code: currency.code,
      label: `${currency.symbol} ${currency.name}`,
    }));
  }, [currenciesResponse]);
  const hasCurrencyOptions = currencyOptions.length > 0;

  const extraFields: GoalFieldConfig[] = [
    {
      field: "status",
      labelKey: "briefingsView.goalForm.status",
      fallback: "Status",
    },
    {
      field: "notes",
      labelKey: "briefingsView.goalForm.notes",
      fallback: "Notes",
      type: "textarea",
    },
  ];

  const outlinedInputStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
    },
  };

  const calculatedFieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      backgroundColor: theme.palette.info.light + "15",
      borderColor: theme.palette.info.main,
      "& fieldset": {
        borderColor: theme.palette.info.main,
        borderWidth: "2px",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.info.dark,
      },
      "&.Mui-disabled": {
        backgroundColor: theme.palette.info.light + "20",
        "& fieldset": {
          borderColor: theme.palette.info.main,
        },
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.info.dark,
      fontWeight: 600,
      "&.Mui-disabled": {
        color: theme.palette.info.dark,
      },
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: theme.palette.text.primary,
      color: theme.palette.text.primary,
    },
  };

  const renderField = (config: GoalFieldConfig) => {
    const {
      field,
      labelKey,
      fallback,
      type = "text",
      disabled = false,
      tooltip,
    } = config;

    if (!shouldShowField(field)) {
      return null;
    }

    // Combine config disabled with role-based disabled
    const isFieldDisabled = disabled || shouldDisableField(field);

    if (field === "country") {
      return (
        <CountrySelect
          label={t(labelKey, fallback)}
          value={formState.country}
          onChange={(value) => handleFieldChange("country", value)}
          error={Boolean(errors[field])}
          helperText={errors[field]}
          size="small"
          disabled={isFieldDisabled}
        />
      );
    }

    const commonProps = {
      fullWidth: true,
      label: t(labelKey, fallback),
      value: formState[field],
      onChange: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      ) => handleFieldChange(field, event.target.value),
      disabled: isFieldDisabled,
      error: Boolean(errors[field]),
      helperText: errors[field],
      size: "small" as const,
      sx: isFieldDisabled ? calculatedFieldStyles : outlinedInputStyles,
      InputProps: tooltip
        ? {
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={tooltip} arrow>
                  <InfoOutlined fontSize="small" color="action" />
                </Tooltip>
              </InputAdornment>
            ),
          }
        : undefined,
    };

    if (field === "adserverCost" && isBeApoloCompany) {
      return (
        <Stack spacing={1}>
          <Typography variant="subtitle2" fontWeight={600}>
            {t("briefingsCreate.form.hasVideo", "Has Video?")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(
              "briefingsCreate.form.hasVideoHelper",
              "Selecting an option pre-fills the Adserver fee, but you can edit it."
            )}
          </Typography>
          <ToggleButtonGroup
            exclusive
            color="primary"
            value={adserverFormat ?? "RICH_MEDIA_NO_VIDEO"}
            onChange={handleAdserverFormatChange}
          >
            <ToggleButton value="VIDEO">
              {t(
                "briefingsCreate.form.hasVideoWithVideo",
                "Rich media with video"
              )}
            </ToggleButton>
            <ToggleButton value="RICH_MEDIA_NO_VIDEO">
              {t(
                "briefingsCreate.form.hasVideoWithoutVideo",
                "Rich media without video"
              )}
            </ToggleButton>
            <ToggleButton value="BANNER">
              {t("briefingsCreate.form.hasVideoBanner", "Banner with AdServer")}
            </ToggleButton>
          </ToggleButtonGroup>
          <TextField
            {...commonProps}
            type="number"
            inputProps={{ step: "any" }}
          />
        </Stack>
      );
    }

    if (field === "adtechCost" && adtechPercentage) {
      const percentageLabel = Math.round(adtechPercentage * 100);
      const helperMessage =
        errors[field] ||
        (adtechCostManuallyEdited
          ? t("briefingsView.goalForm.autoAdtechManualHelper", {
              percentage: percentageLabel,
            })
          : t("briefingsView.goalForm.autoAdtechHelper", {
              percentage: percentageLabel,
            }));
      return (
        <Stack spacing={1}>
          <TextField
            {...commonProps}
            type="number"
            inputProps={{ step: "any" }}
            helperText={helperMessage}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={handleReapplyAdtech}
            disabled={!adtechCostManuallyEdited}
          >
            {t(
              "briefingsView.goalForm.reapplyAdtech",
              "Reapply automatic value"
            )}
          </Button>
        </Stack>
      );
    }

    if (type === "textarea") {
      return <TextField {...commonProps} multiline minRows={3} />;
    }

    if (type === "number") {
      return (
        <TextField
          {...commonProps}
          type="number"
          inputProps={{ step: "any" }}
        />
      );
    }

    return <TextField {...commonProps} />;
  };

  const startDateValue = formState.startDate
    ? dayjs(formState.startDate)
    : null;
  const endDateValue = formState.endDate ? dayjs(formState.endDate) : null;
  const today = dayjs().startOf("day");
  const warnings: string[] = [];

  if (
    startDateValue &&
    endDateValue &&
    startDateValue.isSame(endDateValue, "day")
  ) {
    warnings.push(
      t(
        "briefingsView.goalWarnings.sameDates",
        "¡Cuidado la fecha de inicio y final es la misma!"
      )
    );
  }

  if (
    (startDateValue && startDateValue.isBefore(today, "day")) ||
    (endDateValue && endDateValue.isBefore(today, "day"))
  ) {
    warnings.push(
      t(
        "briefingsView.goalWarnings.pastDates",
        "¡Cuidado la fecha de inicio o fin es anterior al día de hoy!"
      )
    );
  }

  const expectedVolumeNumber = Number(formState.expectedVolume);
  if (!Number.isNaN(expectedVolumeNumber) && expectedVolumeNumber > 1000) {
    warnings.push(
      t(
        "briefingsView.goalWarnings.highVolume",
        "¡Cuidado el volumen previsto es muy alto!"
      )
    );
  }
  if (
    startDateValue &&
    endDateValue &&
    endDateValue.isBefore(startDateValue, "day")
  ) {
    warnings.push(
      t(
        "briefingsView.goalWarnings.endBeforeStart",
        "¡Cuidado! La fecha de fin es anterior a la fecha de inicio."
      )
    );
  }

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
        <DialogTitle
          sx={{
            fontWeight: 700,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          {mode === "create"
            ? t("briefingsView.goalDialogTitleCreate", "Create goal")
            : t("briefingsView.goalDialogTitleEdit", "Edit goal")}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 400 }}
          >
            {t(
              "briefingsView.goalDialogSubtitle",
              "Fill the goal, investment and tracking details for this associated campaign."
            )}
          </Typography>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Stack spacing={3}>
            {warnings.length > 0 && (
              <Alert severity="warning">
                <Stack spacing={0.5}>
                  {warnings.map((warning) => (
                    <Typography key={warning} variant="body2">
                      {warning}
                    </Typography>
                  ))}
                </Stack>
              </Alert>
            )}
            <Box
              sx={{
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 3,
                p: { xs: 2, md: 3 },
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t("briefingsView.goalSections.general", "General information")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    type="date"
                    fullWidth
                    label={t("briefingsView.goalForm.startDate", "Start date")}
                    value={formState.startDate}
                    onChange={(event) =>
                      handleFieldChange("startDate", event.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={
                      shouldDisableField("startDate")
                        ? calculatedFieldStyles
                        : outlinedInputStyles
                    }
                    disabled={shouldDisableField("startDate")}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label={t("briefingsView.goalForm.objective", "Objective")}
                    value={formState.objective}
                    onChange={(event) =>
                      handleFieldChange(
                        "objective",
                        event.target.value as BriefingObjective
                      )
                    }
                    size="small"
                    sx={
                      shouldDisableField("objective")
                        ? calculatedFieldStyles
                        : outlinedInputStyles
                    }
                    disabled={shouldDisableField("objective")}
                  >
                    {objectives.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label={t("briefingsView.goalForm.funnel", "Funnel")}
                    value={formState.funnel}
                    onChange={(event) =>
                      handleFieldChange(
                        "funnel",
                        event.target.value as BriefingFunnel
                      )
                    }
                    size="small"
                    sx={
                      shouldDisableField("funnel")
                        ? calculatedFieldStyles
                        : outlinedInputStyles
                    }
                    disabled={shouldDisableField("funnel")}
                  >
                    {funnels.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    type="date"
                    fullWidth
                    label={t("briefingsView.goalForm.endDate", "End date")}
                    value={formState.endDate}
                    onChange={(event) =>
                      handleFieldChange("endDate", event.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={
                      shouldDisableField("endDate")
                        ? calculatedFieldStyles
                        : outlinedInputStyles
                    }
                    disabled={shouldDisableField("endDate")}
                  />
                </Grid>
                {baseInfoFields.map((config) => (
                  <Grid item xs={12} md={4} key={config.field}>
                    {renderField(config)}
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box
              sx={{
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 3,
                p: { xs: 2, md: 3 },
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t(
                  "briefingsView.goalSections.performance",
                  "Performance metrics"
                )}
              </Typography>
              <Grid container spacing={2}>
                {performanceFields.map((config) => (
                  <Grid item xs={12} md={4} key={config.field}>
                    {renderField(config)}
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box
              sx={{
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 3,
                p: { xs: 2, md: 3 },
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t("briefingsView.goalSections.costs", "Cost breakdown")}
              </Typography>
              <Grid container spacing={2}>
                {costFields.map((config) => (
                  <Grid item xs={12} md={4} key={config.field}>
                    {renderField(config)}
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box
              sx={{
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 3,
                p: { xs: 2, md: 3 },
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t(
                  "briefingsView.goalSections.additional",
                  "Additional information"
                )}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label={t("briefingsView.goalForm.currency", "Currency")}
                    value={formState.currency}
                    onChange={(event) =>
                      handleFieldChange("currency", event.target.value)
                    }
                    size="small"
                    sx={
                      shouldDisableField("currency")
                        ? calculatedFieldStyles
                        : outlinedInputStyles
                    }
                    disabled={shouldDisableField("currency")}
                    helperText={
                      currenciesError
                        ? t(
                            "briefingsView.goalForm.currencyLoadError",
                            "Unable to load currencies"
                          )
                        : undefined
                    }
                  >
                    {hasCurrencyOptions ? (
                      currencyOptions.map((currency) => (
                        <MenuItem key={currency.code} value={currency.code}>
                          {currency.label}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value={formState.currency || ""} disabled>
                        {isCurrenciesLoading
                          ? t("common.loading", "Loading…")
                          : t("common.notAvailable", "N/A")}
                      </MenuItem>
                    )}
                  </TextField>
                </Grid>
                {extraFields.map((config) => (
                  <Grid item xs={12} key={config.field}>
                    {renderField(config)}
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={handleClose} variant="outlined" color="inherit">
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isLoading || isCalculatingMath}
            startIcon={
              isCalculatingMath ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {isCalculatingMath
              ? t("common.calculating", "Calculating...")
              : t("common.save", "Save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Budget exceeded — confirmation dialog */}
      <Dialog
        open={Boolean(pendingPayload)}
        onClose={() => setPendingPayload(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t(
            "briefingsView.budgetWarning.title",
            "¿Superar el presupuesto del briefing?"
          )}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>
            {t(
              "briefingsView.budgetWarning.message",
              "La inversión total de este goal supera el presupuesto total del briefing. ¿Quieres guardar igualmente?"
            )}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setPendingPayload(null)}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={isLoading}
            onClick={async () => {
              if (pendingPayload) {
                await doSubmit(pendingPayload);
                setPendingPayload(null);
              }
            }}
          >
            {t("briefingsView.budgetWarning.confirm", "Guardar igualmente")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GoalDialog;
